// commandSafety.js — dangerous-command denylist for the Code agent's run_command
// (E1-T01, sprint agent-arch-hardening).
//
// THE RISK: run_command runs arbitrary `bash -c` on the user's Mac as the user,
// with NO privilege boundary. The existing injectionScreen.js / hookTemplates
// guards are input-side / pending — NOT wired to this path. This module is the
// missing OUTPUT-of-the-model guard: before we hand a command to the shell, we
// reject the handful of patterns that are ALMOST-ALWAYS catastrophic.
//
// DESIGN — CONSERVATIVE BY CONSTRUCTION. False positives are worse than a missed
// exotic attack here (the approval workflow + the user are the real boundary), so
// every regex is anchored to a target that is essentially never legitimate:
// the filesystem root, a raw block device, the home dir as a whole, the fork bomb,
// pipe-to-shell of a remote fetch, force-push, --no-verify, machine-control verbs.
// A normal `rm -rf node_modules` / `rm -rf ./dist` / a filename containing "rm"
// MUST pass. Pure + synchronous; non-string/empty input is safe.
//
// Patterns adapted from apolo-api/src/agents/injectionScreen.js (HIGH list) and
// apolo/src/lib/hookTemplates.js (anti-self-destruct DANGEROUS list).

// Each rule: [regex, reason]. First match wins. Reasons are user/model-facing
// (Portuguese, matching the rest of agentTools.js error strings) so the re-prompt
// path is actionable.
const RULES = [
  // ── rm against the filesystem root / home / root-glob ──────────────────
  // Anchored so `rm -rf node_modules` or `rm -rf ./dist` never match. The target
  // after the flags must be exactly `/`, `/*`, `~`, or `~/*` (end / whitespace /
  // separator bounded) — never a relative or named path.
  [
    /\brm\s+(?:-[a-z]*\s+)*-?[a-z]*r[a-z]*f?[a-z]*\s+(?:--no-preserve-root\s+)?(?:\/|\/\*|~\/?\*?)(?:\s|;|&|\||$)/i,
    'rm recursivo na raiz do filesystem (/), home (~) ou glob da raiz (/*) — destrutivo',
  ],
  // --no-preserve-root with rm anywhere on the line (the flag exists only to
  // defeat the root-deletion safety; pairing it with rm is a clear red flag).
  [
    /\brm\b[^\n]*--no-preserve-root/i,
    'rm com --no-preserve-root (remove a proteção da raiz) — destrutivo',
  ],

  // ── disk-destroying writes ─────────────────────────────────────────────
  [
    /\bdd\b[^\n]*\bof=\/dev\/(?:disk|sd|hd|nvme|rdisk|mapper)/i,
    'dd escrevendo direto num device de disco (of=/dev/…) — apaga a unidade',
  ],
  [
    /\bmkfs(?:\.\w+)?\b/i,
    'mkfs formata um filesystem — destrutivo',
  ],
  // Shell redirect of output INTO a raw block device.
  [
    />\s*\/dev\/(?:disk|sd|hd|nvme|rdisk)\w*/i,
    'redirecionamento de saída para um device de disco (> /dev/…) — corrompe a unidade',
  ],

  // ── fork bomb ──────────────────────────────────────────────────────────
  // Classic :(){ :|:& };:  — tolerate optional spaces around the pipe/&.
  [
    /:\s*\(\s*\)\s*\{\s*:\s*\|\s*:\s*&\s*\}\s*;\s*:/,
    'fork bomb — trava a máquina esgotando processos',
  ],

  // ── recursive chmod / chown on the filesystem root ─────────────────────
  [
    /\bchmod\s+(?:-[a-z]*\s+)*-?R[a-z]*\s+[0-7]{3,4}\s+\/(?:\s|;|&|\||$)/i,
    'chmod -R na raiz do filesystem (/) — quebra permissões de todo o sistema',
  ],
  [
    /\bchown\s+(?:-[a-z]*\s+)*-?R[a-z]*\s+\S+\s+\/(?:\s|;|&|\||$)/i,
    'chown -R na raiz do filesystem (/) — quebra ownership de todo o sistema',
  ],

  // ── git: --no-verify and force-push (the constitution forbids these) ───
  [
    /\bgit\b[^\n]*--no-verify\b/i,
    'git --no-verify burla o pre-commit guard — proibido pela constituição',
  ],
  [
    /\bgit\s+push\b[^\n]*(?:--force(?:-with-lease)?|(?:^|\s)-f(?:\s|$))/i,
    'git push --force / -f reescreve o histórico remoto — proibido',
  ],

  // ── pipe a remote fetch straight into a shell ──────────────────────────
  [
    /\b(?:curl|wget)\b[^\n]*\|\s*(?:sudo\s+)?(?:sh|bash|zsh|dash|fish)\b/i,
    'baixar e executar direto no shell (curl/wget | sh) — executa código remoto sem inspeção',
  ],

  // ── secret/credential file exfiltration ────────────────────────────────
  // run_command has NO path containment (unlike read_file), so `cat ~/.aws/
  // credentials` etc. would read provider/cloud/git creds off the Mac and ship
  // them to the cloud LLM. These credential paths are essentially never a
  // legitimate target for a coding agent — block any reference to them.
  [
    /(?:~|\$HOME|\/Users\/[^/\s'"]+|\/root)\/\.(?:ssh|aws|gnupg|netrc|npmrc|git-credentials|config\/(?:gh|gcloud)|kube|docker\/config|cargo\/credentials)\b/i,
    'acesso a arquivo de credenciais (~/.ssh, ~/.aws, ~/.npmrc, ~/.git-credentials, ~/.config/gh, …) — exfiltração de segredos',
  ],
  [
    /\/etc\/(?:shadow|sudoers)\b/i,
    'leitura de /etc/shadow ou /etc/sudoers — credenciais do sistema',
  ],

  // ── machine-control verbs ──────────────────────────────────────────────
  // Command-position-anchored: the verb must START a command (line start, after a
  // separator `;`/`&&`/`||`/`|`, or after `sudo`) — NOT appear inside a quoted
  // string like `echo "shutdown the server"`. Killed the false-positive on prose.
  [
    /(?:^|[;&|]|&&|\|\||\bsudo\s+)\s*(?:shutdown|reboot|halt|poweroff)\b/i,
    'shutdown/reboot — desliga ou reinicia a máquina do usuário',
  ],
  [
    /(?:^|[;&|]|&&|\|\||\bsudo\s+)\s*killall\s+-9\b/i,
    'killall -9 — mata processos à força em todo o sistema',
  ],
];

/**
 * Classify a shell command's risk for the Code agent's run_command.
 *
 * @param {string} command
 * @returns {{ blocked: boolean, reason?: string }}
 *   blocked:true with a human-readable `reason` ONLY for high-confidence
 *   destructive/unsafe patterns; `{ blocked: false }` otherwise. Pure + sync.
 */
export function classifyCommandRisk(command) {
  if (typeof command !== 'string' || !command.trim()) return { blocked: false };
  for (const [re, reason] of RULES) {
    if (re.test(command)) return { blocked: true, reason };
  }
  return { blocked: false };
}

// Exported for tests / future tuning.
export const _internals = { RULES };
